import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Slider,
  Grid,
  Paper,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import AddIcon from '@mui/icons-material/Add';
import PreviewIcon from '@mui/icons-material/Preview';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface AddTableModalProps {
  open: boolean;
  onClose: () => void;
  menuId: string;
  menuName: string;
  companyId: string | null;
}

interface ExistingTable {
  id: number;
  tableNumber: number;
  tableName: string | null;
}

const AddTableModal: React.FC<AddTableModalProps> = ({
  open,
  onClose,
  menuId,
  menuName,
  companyId
}) => {
  const { t } = useTranslation('salesSystem');
  const [tableCount, setTableCount] = useState<number>(10);
  const [tablePrefix, setTablePrefix] = useState<string>(t('qrMenuAddTableModal.defaults.tablePrefix'));
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [existingTables, setExistingTables] = useState<ExistingTable[]>([]);

  // Masa sayısını değiştirme
  const handleTableCountChange = (event: Event, newValue: number | number[]) => {
    setTableCount(newValue as number);
    setError('');
  };

  // Input ile masa sayısını değiştirme
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value >= 1 && value <= 1000) {
      setTableCount(value);
      setError('');
    }
  };

  // Masa ön ekini değiştirme
  const handlePrefixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Maksimum 20 karakter, boşluk ve özel karakterlere izin ver
    if (value.length <= 20) {
      setTablePrefix(value);
      setError('');
    }
  };

  // Mevcut masaları yükle
  const loadExistingTables = async () => {
    if (!companyId) return;

    setLoadingExisting(true);
    try {
      const response = await mainApi.get(`/api/Table/company/${companyId}/qrmenu/${menuId}/tables`);
      const result = response.data;
      if (result.success && result.data) {
        setExistingTables(result.data);
        console.log(`✅ ${result.data.length} mevcut masa yüklendi`);
      }
    } catch (error) {
      console.error('Mevcut masalar yüklenirken hata:', error);
    } finally {
      setLoadingExisting(false);
    }
  };

  // Modal açıldığında mevcut masaları yükle
  React.useEffect(() => {
    if (open && companyId && menuId) {
      loadExistingTables();
    }
  }, [open, companyId, menuId]);

  // Masaları oluştur
  const handleCreateTables = async () => {
    if (!companyId) {
      setError(t('qrMenuAddTableModal.errors.companyInfoMissing'));
      return;
    }

    if (tableCount < 1 || tableCount > 1000) {
      setError(t('qrMenuAddTableModal.errors.tableCountRange'));
      return;
    }

    if (!tablePrefix.trim()) {
      setError(t('qrMenuAddTableModal.errors.prefixRequired'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log(`🔄 ${tableCount} masa oluşturuluyor (Şirket: ${companyId}, Menü: ${menuId}, Ön Ek: ${tablePrefix})`);
      console.log(`📋 Modal'dan gelen veriler:`);
      console.log(`  - menuId: "${menuId}" (tip: ${typeof menuId})`);
      console.log(`  - menuName: "${menuName}"`);
      console.log(`  - companyId: "${companyId}" (tip: ${typeof companyId})`);
      console.log(`  - tableCount: ${tableCount}`);
      console.log(`  - tablePrefix: "${tablePrefix}"`);
      
      // SQL API çağrısı
      const fullUrl = `/api/Table/company/${companyId}/qrmenu/${menuId}/create-tables`;
      console.log(`🌐 API URL: ${fullUrl}`);
      
      const response = await mainApi.post(fullUrl, {
        tableCount: tableCount,
        tablePrefix: tablePrefix.trim()
      });

      const result = response.data;

      if (result.success) {
        console.log(`✅ ${tableCount} masa başarıyla oluşturuldu:`, result.data);
        
        const successMsg = result.data.existingTableCount > 0
          ? t('qrMenuAddTableModal.messages.tablesAddedWithTotal', {
              count: result.data.tableCount,
              start: result.data.startNumber,
              end: result.data.endNumber,
              total: result.data.totalTableCount
            })
          : t('qrMenuAddTableModal.messages.tablesCreated', { count: result.data.tableCount });
        
        setSuccess(successMsg);
        
        // Mevcut masaları yenile
        await loadExistingTables();
        
        // 3 saniye sonra modal'ı kapat
        setTimeout(() => {
          onClose();
          handleReset();
        }, 3000);
      } else {
        console.error('❌ Masa oluşturma API hatası:', result);
        setError(result.message || t('qrMenuAddTableModal.errors.createFailed'));
      }
      
    } catch (err) {
      console.error('❌ Masa oluşturma ağ hatası:', err);
      setError(t('qrMenuAddTableModal.errors.createFailedRetry'));
    } finally {
      setLoading(false);
    }
  };

  // Modal'ı sıfırla
  const handleReset = () => {
    setTableCount(10);
    setTablePrefix(t('qrMenuAddTableModal.defaults.tablePrefix'));
    setError('');
    setSuccess('');
    setLoading(false);
    setExistingTables([]);
  };

  // Modal kapanırken sıfırla
  const handleClose = () => {
    if (!loading) {
      handleReset();
      onClose();
    }
  };

  // Masa numaralarını önizleme
  const getTablePreview = () => {
    const maxExistingNumber = existingTables.length > 0 
      ? Math.max(...existingTables.map(t => t.tableNumber))
      : 0;
    const startNumber = maxExistingNumber + 1;
    
    if (tableCount <= 10) {
      return Array.from({ length: tableCount }, (_, i) => startNumber + i);
    } else {
      // İlk 5 ve son 5 masayı göster
      const first5 = Array.from({ length: 5 }, (_, i) => startNumber + i);
      const last5 = Array.from({ length: 5 }, (_, i) => startNumber + tableCount - 5 + i);
      return [...first5, '...', ...last5];
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableRestaurantIcon color="warning" />
          <Typography variant="h6" component="div">
            {t('qrMenuAddTableModal.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('qrMenuAddTableModal.subtitle', { menuName })}
        </Typography>
        {loadingExisting ? (
          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
            {t('qrMenuAddTableModal.loadingExisting')}
          </Typography>
        ) : existingTables.length > 0 ? (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark" sx={{ fontWeight: 'bold' }}>
              {t('qrMenuAddTableModal.existingStatus.title', { count: existingTables.length })}
            </Typography>
            <Typography variant="caption" color="info.dark" sx={{ display: 'block', mt: 0.5 }}>
              {t('qrMenuAddTableModal.existingStatus.details', {
                lastTable: existingTables[0]?.tableName || t('qrMenuAddTableModal.preview.tableWithNumber', {
                  prefix: t('qrMenuAddTableModal.defaults.tablePrefix'),
                  number: Math.max(...existingTables.map(t => t.tableNumber))
                }),
                startNumber: Math.max(...existingTables.map(t => t.tableNumber)) + 1
              })}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
            {t('qrMenuAddTableModal.existingStatus.firstTables')}
          </Typography>
        )}
        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}>
          🔍 Debug: QR Menü ID = {menuId}
        </Typography>
        <Typography variant="caption" color="secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
          🏢 Şirket ID = {companyId}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Sol Taraf - Masa Sayısı Seçimi */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="primary" />
                {t('qrMenuAddTableModal.sections.tableInfo')}
              </Typography>
              
              {/* Masa Ön Eki */}
              <Box sx={{ mt: 3, mb: 3 }}>
                <TextField
                  label={t('qrMenuAddTableModal.fields.prefix')}
                  value={tablePrefix}
                  onChange={handlePrefixChange}
                  fullWidth
                  variant="outlined"
                  placeholder={t('qrMenuAddTableModal.fields.prefixPlaceholder')}
                  helperText={t('qrMenuAddTableModal.fields.prefixHelp')}
                  inputProps={{ maxLength: 20 }}
                />
              </Box>
              
              <Box sx={{ mt: 3, mb: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('qrMenuAddTableModal.fields.countQuestion')}
                </Typography>
                
                {/* Slider */}
                <Box sx={{ px: 2, mt: 3 }}>
                  <Slider
                    value={tableCount}
                    onChange={handleTableCountChange}
                    min={1}
                    max={1000}
                    step={1}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 250, label: '250' },
                      { value: 500, label: '500' },
                      { value: 750, label: '750' },
                      { value: 1000, label: '1000' }
                    ]}
                    valueLabelDisplay="auto"
                    color="warning"
                  />
                </Box>
              </Box>

              {/* Manuel Giriş */}
              <TextField
                label={t('qrMenuAddTableModal.fields.count')}
                type="number"
                value={tableCount}
                onChange={handleInputChange}
                inputProps={{ min: 1, max: 1000 }}
                fullWidth
                variant="outlined"
                helperText={t('qrMenuAddTableModal.fields.countHelp')}
              />

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Chip 
                  label={t('qrMenuAddTableModal.preview.countBadge', { count: tableCount })}
                  color="warning"
                  variant="outlined"
                  size="medium"
                  sx={{ fontSize: '1rem', padding: '8px 16px', height: 'auto' }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Sağ Taraf - Önizleme */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PreviewIcon color="info" />
                {t('qrMenuAddTableModal.sections.preview')}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('qrMenuAddTableModal.preview.createdTableNumbers')}
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 200, overflowY: 'auto' }}>
                {getTablePreview().map((tableNum, index) => (
                  <Chip
                    key={index}
                    label={
                      tableNum === '...'
                        ? '...'
                        : t('qrMenuAddTableModal.preview.tableWithNumber', { prefix: tablePrefix, number: tableNum })
                    }
                    variant={tableNum === '...' ? 'outlined' : 'filled'}
                    color={tableNum === '...' ? 'default' : 'primary'}
                    size="small"
                    sx={{ 
                      fontSize: '0.75rem',
                      ...(tableNum === '...' && { 
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'text.secondary'
                      })
                    }}
                  />
                ))}
              </Box>

              {tableCount > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {t('qrMenuAddTableModal.preview.rangeSummary', {
                    count: tableCount,
                    prefix: tablePrefix,
                    start: getTablePreview()[0],
                    end: getTablePreview()[getTablePreview().length - 1]
                  })}
                  {existingTables.length > 0
                    && ` ${t('qrMenuAddTableModal.preview.totalSummary', { total: existingTables.length + tableCount })}`}
                </Typography>
              )}

              {existingTables.length === 0 && tableCount <= 10 && (
                <Typography variant="caption" color="success.main" sx={{ mt: 2, display: 'block' }}>
                  {t('qrMenuAddTableModal.preview.countBadge', { count: tableCount })}
                </Typography>
              )}

              {existingTables.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="success.dark" sx={{ fontWeight: 'bold', display: 'block' }}>
                    {t('qrMenuAddTableModal.preview.finalTotal', { total: existingTables.length + tableCount })}
                  </Typography>
                  <Typography variant="caption" color="success.dark" sx={{ display: 'block' }}>
                    {t('qrMenuAddTableModal.preview.totalBreakdown', {
                      existing: existingTables.length,
                      newCount: tableCount
                    })}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.dark">
                  <strong>{t('qrMenuAddTableModal.infoLabel')}</strong> {t('qrMenuAddTableModal.infoText')}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          {t('qrMenuAddTableModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleCreateTables}
          variant="contained"
          color="warning"
          disabled={loading || tableCount < 1 || tableCount > 1000 || !tablePrefix.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <TableRestaurantIcon />}
        >
          {loading
            ? t('qrMenuAddTableModal.actions.creating')
            : t('qrMenuAddTableModal.actions.createWithCount', { count: tableCount, prefix: tablePrefix })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTableModal;
